﻿import {useEffect, useMemo, useState} from "react";

interface UseReportDataSummaryCollapseByIndexParams {
  data: any[] | undefined
}

interface UseReportDataSummaryCollapseByIndex {
  collapseByIndex: boolean[];
  handleToggleCollapse: (index: number) => void;
  hasExpandedItems: boolean;
  setExpandedItems: (items: Record<string, unknown>) => void;
  setExpandedAll: () => void;
  handleExpandAll: () => void;
}

/**
 * Hook to help with the usage of expand/collapse functionality in ReportDataSummary when there are no ids
 * @param data any array of objects to watch and create expand/collapse state for
 *
 * @see {@link useCollapseById} when items have unique ids
 */
const useReportDataSummaryCollapseByIndex: (args: UseReportDataSummaryCollapseByIndexParams) => UseReportDataSummaryCollapseByIndex = ({ data }) => {
  // ReportDataSummary tracks expansion via a boolean array indexed by position (no item ids available)
  const [collapseByIndex, setCollapseByIndex] = useState<boolean[]>([]);

  useEffect(() => {
    if (data?.length) {
      setCollapseByIndex(data.map(() => false));
    }
  }, [data, setCollapseByIndex]);

  const handleToggleCollapse = (index: number) => {
    setCollapseByIndex(prevCollapse => (prevCollapse.with(index, !prevCollapse[index])));
  }

  const handleExpandAll = () => {
    setCollapseByIndex(prevCollapse => (prevCollapse.map(() => true)));
  }

  const handleCollapseAll = () => {
    setCollapseByIndex(prevCollapse => (prevCollapse.map(() => false)));
  }

  const setExpandedItems = (_items: Record<string, unknown>) => {};

  const hasExpandedItems = useMemo(() => collapseByIndex.includes(true), [collapseByIndex]);

  return {
    collapseByIndex,
    handleToggleCollapse,
    handleExpandAll,
    setExpandedAll: handleCollapseAll,
    hasExpandedItems,
    setExpandedItems,
  }
}

export { useReportDataSummaryCollapseByIndex };
